import type { ImportRow } from '../../api/uat';
import type { ReadSheet } from '../../lib/xlsxRead';

// A UAT plan workbook, read into rows for the API to plan and apply. Two layouts are recognized,
// by header name and never by position:
//
//   Jones Day — one sheet per section ("2. Company Record"), a header row starting "Test ID", a
//     whole-number row per section and "2.04"-style rows per step.
//   ClientVerse template — a "Test Cases" sheet whose Test Suite column is the section.
//
// Text is passed through raw: lifting CONFIRMs and padding Test IDs is the API's job, so there is
// one implementation of each rule. Result columns (Pass, Fail, Tester Notes) are ignored.

export interface PlanParse {
  rows: ImportRow[];
  sheets: string[];
  skipped: string[];
  /** A sheet that looks like part of the plan but can't be read as one. These block the import. */
  errors: string[];
  /** Things worth a look that don't stop the import: columns that are absent, sheets set aside by name. */
  warnings: string[];
}

const norm = (s: string | undefined) => (s ?? '').replace(/\s+/g, ' ').trim();
const key = (s: string | undefined) => norm(s).toLowerCase();

/**
 * Excel can store 2.01 as 2.0099999999999998. Such a value is rounded back to the ID it was typed
 * as; a short numeric like "2.1" (from 2.10) is left for the API to pad.
 */
export function cleanTestId(raw: string): string {
  const s = norm(raw);
  return /^\d+\.\d{5,}$/.test(s) ? String(Number(Number(s).toFixed(4))) : s;
}

interface Columns {
  testId: number;
  role: number;
  action: number;
  expected: number;
  miComment: number;
  suite: number;
  caseName: number;
  prerequisites: number;
}

function columns(header: string[]): Columns {
  const find = (...tests: ((h: string) => boolean)[]) => header.findIndex((h) => tests.some((t) => t(key(h))));
  return {
    testId: find((h) => h === 'test id'),
    role: find((h) => h === 'role', (h) => h === 'persona'),
    action: find((h) => h.startsWith('description'), (h) => h.includes('test action'), (h) => h === 'test steps'),
    expected: find((h) => h.startsWith('expected result')),
    miComment: find((h) => h === 'mi comments', (h) => h === 'mi comment'),
    suite: find((h) => h === 'test suite'),
    caseName: find((h) => h === 'test case name'),
    prerequisites: find((h) => h === 'prerequisites'),
  };
}

// Sheets that hold issues or reference data rather than steps are set aside by name, and often have
// a Test ID column of their own. Only these exact names are set aside quietly; a sheet that merely
// contains one of the words ("2. Data Summary") is set aside too, as before, but with a warning.
const SET_ASIDE = /issue tracker|defects|summary|test data/i;
const KNOWN_TRACKER = /^(?:uat )?(?:issue tracker|defects|summary|test data)$/;
const plainName = (name: string) => key(name).replace(/^\d+\s*[.)]\s*/, '');
const list = (names: string[]) => names.map((n) => `“${n}”`).join(', ');

export function parsePlan(sheets: ReadSheet[]): PlanParse | null {
  const rows: ImportRow[] = [];
  const used: string[] = [];
  const skipped: string[] = [];
  const errors: string[] = [];
  const warnings: string[] = [];
  const noRole: string[] = [];
  const noMiComments: string[] = [];
  const suiteCodes = new Map<string, string>();

  for (const sheet of sheets) {
    const h = sheet.rows.findIndex((r) => r.some((c) => key(c) === 'test id'));
    if (SET_ASIDE.test(sheet.name)) {
      skipped.push(sheet.name);
      if (h >= 0 && !KNOWN_TRACKER.test(plainName(sheet.name))) {
        const c = columns(sheet.rows[h]);
        if (c.expected >= 0 && (c.action >= 0 || c.caseName >= 0)) {
          warnings.push(`“${sheet.name}” was set aside because its name contains “issue tracker”, “defects”, “summary” or “test data”, but it has Test ID and Expected Result columns. If it holds test steps, rename the sheet.`);
        }
      }
      continue;
    }
    if (h < 0) { skipped.push(sheet.name); continue; }
    const col = columns(sheet.rows[h]);
    const hasAction = col.action >= 0 || col.caseName >= 0;
    const hasExpected = col.expected >= 0;
    // Neither: not a plan sheet. Only one of the two: a plan sheet whose column was renamed, which
    // must not vanish from the import without a word.
    if (!hasAction && !hasExpected) { skipped.push(sheet.name); continue; }
    if (!hasAction || !hasExpected) {
      const found = sheet.rows[h].map(norm).filter(Boolean).join(', ');
      errors.push(`Sheet “${sheet.name}” has a Test ID column but no ${hasExpected ? '“Description / Test Action”' : '“Expected Result”'} column (it has: ${found}). Rename the column in the workbook, or rename or remove the sheet if it isn’t a test sheet.`);
      continue;
    }
    used.push(sheet.name);
    if (col.role < 0) noRole.push(sheet.name);
    if (col.miComment < 0) noMiComments.push(sheet.name);

    const named = /^(\d+)\s*[.)]\s*(.+)$/.exec(norm(sheet.name));
    let code = named?.[1] ?? String(used.length);
    let title = named?.[2] ?? norm(sheet.name);
    const cell = (r: string[], i: number) => (i >= 0 ? norm(r[i]) : '');

    for (const r of sheet.rows.slice(h + 1)) {
      const testId = cleanTestId(cell(r, col.testId));
      if (!testId) continue;
      if (/^\d+$/.test(testId)) {
        // A section row: "2 | | Company Record".
        code = testId;
        title = cell(r, col.action) || title;
        continue;
      }
      let sectionCode = code;
      let sectionTitle = title;
      const suite = cell(r, col.suite);
      if (suite) {
        if (!suiteCodes.has(suite)) suiteCodes.set(suite, String(suiteCodes.size + 1));
        sectionCode = suiteCodes.get(suite)!;
        sectionTitle = suite;
      }
      const action = [cell(r, col.caseName), cell(r, col.action), cell(r, col.prerequisites) && `Before you start: ${cell(r, col.prerequisites)}`]
        .filter(Boolean).join('\n');
      const expected = cell(r, col.expected);
      if (!action && !expected) continue;
      rows.push({
        sectionCode,
        sectionTitle,
        testId,
        role: cell(r, col.role) || null,
        action,
        expected,
        miComment: cell(r, col.miComment) || null,
      });
    }
  }
  if (noRole.length) warnings.push(`No Role or Persona column on ${list(noRole)}: those steps have no persona, so they don’t count in coverage by persona.`);
  if (noMiComments.length) warnings.push(`No “MI Comments” column on ${list(noMiComments)}: a “confirm before release” note there can’t be picked up. Only [CONFIRM …] written in a step’s own text is.`);
  return rows.length || errors.length ? { rows, sheets: used, skipped, errors, warnings } : null;
}
